import {Modal, Button, Input, message} from "antd";
import ApiService from "../../../utils/ApiService";
import {convertBase64ToBlob, getParsedNumber, isOnlySpecialCharacters} from "../../../utils/Common";
import FoodAddEditForm from "./FoodAddEditFormComponent";
import FoodDiscount from "./FoodDiscountComponent";
import FoodVariantAddOns from "./FoodVariantAddOnsComponent";
import FoodImageUploaderWithCropper from "./FoodImageUploaderWithCropper";
import {
  buttonStyleNegative,
  buttonStylePositive,
  defaultFoodOption,
  validateFoodFrom,
  makeSingleFoodOptionDefaultSelected,
  mergeFoodVariantsWithCategory,
} from "../../../services/food-service";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import useUserStore from "../../../stores/store";
import { useNavigate } from "react-router-dom";
import { ExclamationCircleOutlined, RightCircleOutlined } from "@ant-design/icons";
import FoodMealAddition from "./FoodMealAddition";
import ImageUploadWithCropAndWebp from "../../common/ImageUploadWithCropAndWebp";
import {CharLimit as charLimit} from "../../../constant/constants";
import FoodTaxAndDiscount from "./FoodTaxAndDiscount";

function FoodAddEditComponent({ categoryList, categoryDropdownList }) {
  const [isEditMode, setIsEditMode] = useState(false);
  const [categoryId, setCategoryId] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [defaultQuantity, setDefaultQuantity] = useState(10);
  const [discount, setDisCount] = useState("");
  const [isDiscountInPercent, setIsDiscountInPercent] = useState("");
  const [options, setOptions] = useState([defaultFoodOption]);
  const [addOns, setAddOns] = useState([]);
  const [foodAdditionGroups, setFoodAdditionGroups] = useState({additionGroups: []});
  const [images, setImages] = useState([]);
  const [image, setImage] = useState(null);
  const [modal, contextHolder] = Modal.useModal();

  const { user, restaurantId, setNetworkCall, showNotification } = useUserStore();
  const { foodId } = useParams();

  const [isVatApplicable, setIsVatApplicable] = useState(true);
  const [isDiscountApplicable, setIsDiscountApplicable] = useState(true);
  const [isSupplementaryDutyApplicable, setIsSupplementaryDutyApplicable] = useState(false);
  const [supplementaryDuty, setSupplementaryDuty] = useState("");

  const navigate = useNavigate();

  const onCategoryChange = (val) => {
    const selectedCat = categoryList.find((item) => item.id === val);

    if (selectedCat === undefined) {
      return;
    }

    setCategoryId(val);
  };

  const handleCancel = () => {
    navigate("/food");
  };

  const clearFields = () => {
    setCategoryId("");
    setName("");
    setDefaultQuantity(10);
    setDescription("");
    setDisCount("");
    setIsDiscountInPercent("");
    setOptions([defaultFoodOption]);
    setAddOns([]);
    setImages([]);
  };

  const fetchFoodDetails = async () => {
    setNetworkCall(true);

    const res = await ApiService.GET_FOOD_DETAILS(foodId, user.token.token);

    if (res.status === "ok") {
      const data = res.payload;
      setCategoryId(data.categoryId);
      setName(data.name);
      setDescription(data.details);
      setDefaultQuantity(data.initialQuantity);
      setDisCount(data.discount);
      setIsDiscountInPercent(data.isDiscountInPercent);
      setAddOns(data.addOns);
      setFoodAdditionGroups({ additionGroups: data.additionGroups });

      setIsVatApplicable(data.isVatApplicable);
      setIsDiscountApplicable(data.isDiscountApplicable);
      setIsSupplementaryDutyApplicable(data.isSupplementaryDutyApplicable);
      setSupplementaryDuty(data.supplementaryDuty);

      const mergedVariants = mergeFoodVariantsWithCategory(data.options, data.categoryId, categoryList);
      setOptions(mergedVariants);

      const transformedData = data.images.map((item) => ({
        ...item,
        url: `${item.path}`,
      }));
      setImages(transformedData);
    } else {
      showNotification(res.data.message);
    }

    setNetworkCall(false);
  };

  const handleFoodAddOrEdit = async () => {
    const variants = makeSingleFoodOptionDefaultSelected(options);

    let selectedOptions = variants.filter((item) => item.isSelected).map(({ isDisabled, ...rest }) => rest);

    const validationData = {
      categoryId,
      name,
      allOptions: options,
      options: selectedOptions,
      addOns,
      foodAdditionGroups,
      images,
      discount,
      isDiscountInPercent,
    };

    const error = validateFoodFrom(validationData);

    if (error) {
      showNotification(error);
      return;
    }

    // Prevent names made of only special characters
    if (isOnlySpecialCharacters(name)){
      message.error("name cannot contain only special characters");
      return;
    }

    const formData = new FormData();

    formData.append("foodId", foodId || '');
    formData.append("restaurantId", restaurantId);
    formData.append("categoryId", categoryId);
    formData.append("name", name);
    formData.append("defaultQuantity", defaultQuantity);
    formData.append("discount", getParsedNumber(discount));
    formData.append("isDiscountInPercent", isDiscountInPercent ? "Yes" : "No");
    formData.append("details", description);
    formData.append("foodOptions", JSON.stringify({ options: selectedOptions }));
    formData.append("foodAddOns", JSON.stringify({ addOns }));
    formData.append("foodAdditionGroups", JSON.stringify(foodAdditionGroups));

    formData.append("isVatApplicable", isVatApplicable ? "YES" : "NO");
    formData.append("isDiscountApplicable", isDiscountApplicable ? "YES" : "NO");
    formData.append(
      "isSupplementaryDutyApplicable",
      isSupplementaryDutyApplicable ? "YES" : "NO"
    );
    formData.append(
      "supplementaryDuty",
      isSupplementaryDutyApplicable ? supplementaryDuty : 0
    );
    
    if (images.length > 0) {
      formData.append("foodImage", images[0].originFileObj);
    }

    setNetworkCall(true);
    const res = await ApiService.FOOD_UPSERT(formData, user.token.token);

    setNetworkCall(false);

    setNetworkCall(false);

    // Showing API side error
    if (res.code !== 200) {
      showNotification(res.data.message);
      return;
    }

    const { payload } = res;
    const { dependentOrders, dependentSetMenus, dependentSetMenuOrders, success } = payload;

    if (success) {
      const message = isEditMode ? "Food updated successfully" : "Food added successfully";
      if (!isEditMode) clearFields();

      showNotification(message, false);
      navigate('/food');
    }


    const mergedDependentOrdersSetmenus = [...dependentOrders, ...dependentSetMenuOrders];

    // Dependency checking
    if (mergedDependentOrdersSetmenus.length > 0) {
      const title = `This food is being used in following orders. You can update it after the orders are completed.`;
      FoodEditExceptionModal(title, "orders", mergedDependentOrdersSetmenus);
    }

    if (dependentSetMenus.length > 0) {
      const title = "This food is being used in following set menus, Please update the set menu items first!";
      FoodEditExceptionModal(title, "setmenus", dependentSetMenus);
    }
  };

  const deleteImage = async () => {
    try {
      if (!foodId) {
        setImages([]);
        setImage(null)
        return
      }

      setNetworkCall(true);
      const token = user?.token?.token;
      const params = {
        context: "FOOD_EDIT",
        fieldName: "image",
        modelId: foodId,
      }
      const res = await ApiService.ADMIN_FILE_DELETE(params, token);

      if (res.code !== 200) {
        showNotification(res.data.message)
        return;
      }

      if (res.code === 200) {
        setImages([])
        setImage(null)
  
        showNotification("Image deleted successfully.", false)
        return;
      }
      
    } catch (error) {
      console.error("Food image delete ==== ", error)
      showNotification(error.message || "An unexpected error occurred.")
    } finally {
      setNetworkCall(false);
    }
  }

  useEffect(() => {
    const fetchInitialData = async () => {
      if (foodId) {
        await fetchFoodDetails();
        setIsEditMode(true);
      } else {
        setIsEditMode(false);
      }
    };

    fetchInitialData();
  }, [foodId]);

  /**
   *
   * @param {title} title
   * @param {dependencyType} dependencyType: orders, setmenus
   * @param {data} data: array of objects with id and name/orderNumber
   */
  function FoodEditExceptionModal(_title, dependencyType, data) {
    const formattedData = data.map((item) => {
      if (dependencyType === "orders") {
        return {
          id: item.id,
          title: item.orderNumber,
          url: `/order/${item.id}/edit?foodIdEdit=${foodId}`,
        };
      }
      if (dependencyType === "setmenus") {
        return {
          id: item.id,
          title: item.name,
          url: `/set-menu/edit/${item.id}?foodIdEdit=${foodId}`,
        };
      }
    });
    modal.confirm({
      title: _title,
      icon: <ExclamationCircleOutlined />,
      content: (
        <div className="my-3">
          {Array.isArray(formattedData) &&
            formattedData.map((item) => (
              <div
                key={item.id}
                className="w-full flex items-center justify-between py-2 px-3
               border border-[1px] border-[#FCBD10] rounded-lg mb-1.5 cursor-pointer font-medium"
                onClick={() => navigate(item.url)}
              >
                <p>{item.title}</p>
                <RightCircleOutlined />
              </div>
            ))}
        </div>
      ),
      okText: "Okay",
      cancelText: "Close",
      okButtonProps: { style: { display: "none" } },
      cancelButtonProps: {
        style: { backgroundColor: "#EE3169", color: "white", fontWeight: 700 },
      },
      width: 600,
    });
  }

  return (
    <div className="flex flex-col bg-white rounded-[12px] shadow-sm px-20 py-6">
      <FoodAddEditForm
        records={{
          isEditMode: isEditMode,
          categoryList,
          categoryDropdownList,
          categoryId: categoryId,
          setCategoryId: onCategoryChange,
          restaurantId: restaurantId,
          name: name,
          setName,
          description: description,
          setDescription,
          defaultQuantity,
          setDefaultQuantity,
          setOptions,
        }}
      />

      <FoodDiscount
        records={{
          discount: discount,
          isDiscountInPercent: isDiscountInPercent,
          setDisCount,
          setIsDiscountInPercent,
        }}
      />

      <FoodVariantAddOns
        records={{
          context: "VARIANTS",
          title: "Food Variants",
          placeholder: "Food Variants Name (Char limit 100)",
          isDisabled: true,
          data: options,
          setOptions,
        }}
      />

      <FoodVariantAddOns
        records={{
          context: "ADD-ONS",
          title: "Add-Ons",
          placeholder: "Add-ons Name (Char limit 100)",
          isDisabled: false,
          data: addOns,
          setAddOns,
        }}
      />

      {/*Meal Addition Section*/}
      <FoodMealAddition
        foodAdditionGroups={foodAdditionGroups}
        setFoodAdditionGroups={setFoodAdditionGroups}
      />


      <ImageUploadWithCropAndWebp fileList={images} setFileList={setImages} deleteImage={deleteImage} minWidth={500}/>

      {/*Food Tax And Discount*/}
      <FoodTaxAndDiscount
        records={{
          isVatApplicable,
          setIsVatApplicable,
          isDiscountApplicable,
          setIsDiscountApplicable,
          isSupplementaryDutyApplicable,
          setIsSupplementaryDutyApplicable,
          supplementaryDuty,
          setSupplementaryDuty,
        }}
      />

      <div className="flex flex-row flex-nowrap items-center justify-center gap-x-[20px]">
        {/* start Save Button */}
        <Button type="primary" htmlType="submit" style={buttonStylePositive} onClick={handleFoodAddOrEdit}>
          {isEditMode ? "Update" : "Add Food"}
        </Button>
        {/* end Save Button */}

        {/* start Cancel button */}
        <Button type="primary" htmlType="submit" style={buttonStyleNegative} onClick={handleCancel}>
          Cancel
        </Button>
        {/*  Cancel button */}
      </div>
      {contextHolder}
    </div>
  );
}

export default FoodAddEditComponent;
